import {
  Box,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  Page,
  PageRoute,
  TabContext,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Form, Permission, SurveyEditResource, SurveyViewResource } from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { SurveyGeneralInfo } from "./General/SurveyGeneralInfo";
import { SurveyQuestions } from "./Questions/SurveyQuestions";
import { SurveySections } from "./Sections/SurveySections";
import { SurveyPageHeader } from "./SurveyPageHeader";
import { SurveyPageTabs } from "./SurveyPageTabs";

const routeName = "survey";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

class OptionalParams {
  tab?: SurveyTab | undefined = undefined;
}

export const surveyPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Survey",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export type SurveyTab = "General" | "Questions" | "Sections";

const ROOT_PERMISSION: Permission = Permission.Form;

export const SurveyPage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState<SurveyTab>(optionalParams.tab || "General");
  const [survey, setSurvey] = useState<Form>({} as Form);
  const [refreshSurvey, setRefreshSurvey] = useState<Form>();
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [survey] });
  const [viewResource, setViewResource] = useState<SurveyViewResource>({} as SurveyViewResource);
  const [editResource, setEditResource] = useState<SurveyEditResource>({});
  const [copySurvey, setCopySurvey] = useState<number>();
  const { validationErrors, setValidationErrors } = useValidationErrors();

  // keeps track of which tabs data are loaded
  const [visitedTabs, setVisitedTabs] = useState<SurveyTab[]>([]);

  useEffect(() => {
    fetchSurvey(params.id);
    fetchViewResource(params.id);
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  // load data from each tab as it's selected
  useEffect(() => {
    if (!visitedTabs.includes(currentTab)) {
      setVisitedTabs(
        produce((draft) => {
          draft.push(currentTab);
        })
      );
    }
  }, [currentTab]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${surveyPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetchRefreshSurvey = (id: number) => {
    ApiService.surveys.get(id).then((updatedSurvey) => {
      setRefreshSurvey(updatedSurvey);
      resetUnsavedChanges([updatedSurvey]);
    });
  };

  const fetchSurvey = (id: number) => {
    ApiService.surveys.get(id).then((response) => {
      setSurvey(response);
      setRefreshSurvey(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.surveys.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.surveys.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  return (
    <TabContext value={currentTab}>
      <Page
        isForm
        header={
          <SurveyPageHeader
            rootPermission={ROOT_PERMISSION}
            crudMode={params.crudMode}
            fetch={fetchSurvey}
            fetchRefreshSurvey={fetchRefreshSurvey}
            reroute={reroute}
            survey={survey}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
          />
        }
        tabs={<SurveyPageTabs viewResource={viewResource} setCurrentTab={setCurrentTab} />}
      >
        <Box hidden={currentTab !== "General"}>
          <SurveyGeneralInfo
            survey={survey}
            setSurvey={setSurvey}
            setCopySurvey={setCopySurvey}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
          />
        </Box>

        <Box hidden={currentTab !== "Questions"}>
          {survey && (
            <SurveyQuestions
              survey={survey}
              copySurvey={copySurvey}
              setSurvey={setSurvey}
              refreshSurvey={refreshSurvey}
              viewResource={viewResource}
              editResource={editResource}
              crudMode={params.crudMode}
              validationErrors={validationErrors}
              navigateEditMode={() => {
                navigate(`../${surveyPageRoute.getPath({ id: params.id, crudMode: "edit" })}`);
              }}
            />
          )}
        </Box>

        <Box hidden={currentTab !== "Sections"}>
          {survey && (
            <SurveySections
              survey={survey}
              copySurvey={copySurvey}
              refreshSurvey={refreshSurvey}
              setSurvey={setSurvey}
              crudMode={params.crudMode}
              validationErrors={validationErrors}
            />
          )}
        </Box>
      </Page>
    </TabContext>
  );
};
